import React from 'react';
import { translate } from 'react-i18next';
import { showTip, appDefIds } from '@wix/business-manager-api';

import Play from 'wix-ui-icons-common/Play';
import Pause from 'wix-ui-icons-common/Pause';
import Delete from 'wix-ui-icons-common/Delete';
import Replace from 'wix-ui-icons-common/Replace';
import Edit from 'wix-ui-icons-common/Edit';
import { redirect } from '../../client/utils/dom';

import { getStores } from '../../client/stores';
import { integrationStatus } from '../../client/api';
import EmbeddedIntegrationContent from '../../client/components/modals/embedded-integration-content';
import { key as fbPixelKey } from '../facebook-pixel/manifest';
import { Text, Box } from 'wix-style-react';

export default ({
  key,
  integrationConfig,
  dashboardWidget,
  detailsPage,
  modal,
  requiredPermissions,
}) => ({
  key,
  basic: dashboardWidget,
  requiredPermissions,
  detailed: (api) => {
    const { biLogger } = getStores();
    const biData = {
      integrationType: key,
      modalType: 'add ID modal',
    };
    const SubTitle = translate()(({ t }) => (
      <Text secondary>{t(modal.subtitle)}</Text>
    ));
    const openEditModal = () => {
      modal.type === 'flow' &&
        api.openFlowModal({
          title: modal.title,
          subTitleNode: <SubTitle />,
          contentNode: (
            <EmbeddedIntegrationContent
              integrationConfig={integrationConfig}
              api={api.embeddedIntegrations}
            />
          ),
          onOpened: () => biLogger.integrationModalView(biData),
          onClosed: (modalAPI) => {
            const { content } = modalAPI.getParams().integrationData;
            const data = { trackingId: content.trackingId };
            biLogger.integrationModalClosed({ ...biData, data });
          },
          buttons: {
            primary: {
              text: modal.save || 'modal.button.save',
              onClick: (modalAPI) => {
                const modalParams = modalAPI.getParams();
                if (modalParams.isValid) {
                  api.embeddedIntegrations
                    .set(key, modalParams.integrationData)
                    .then(() => updateIntegrationStore());
                  modalAPI.close();
                  key === fbPixelKey &&
                    showTip({
                      sourceApplication: appDefIds.marketingIntegration,
                    });
                  biLogger.integrationModalCtaClick({
                    ...biData,
                    ctaType: 'save',
                  });
                } else {
                  modalParams.isDirty = true;
                  modalAPI.setParams(modalParams);
                }
              },
            },
            secondary: {
              text: modal.close || 'modal.button.close',
              onClick: (modalAPI) => {
                modalAPI.close();
                biLogger.integrationModalCtaClick({
                  ...biData,
                  ctaType: 'close',
                });
              },
            },
            x: {
              onClick: () => biLogger.integrationModalCloseViaCloseBar(biData),
            },
          },
        });

      modal.type === 'notification' &&
        api.openNotificationModal({
          title: modal.title,
          subTitle: modal.subtitle,
          illustration: <img src={modal.illustration} alt="" />,
          onOpened: (modalAPI) => {
            biLogger.integrationModalView(biData);
            api.embeddedIntegrations.createDraft(integrationConfig);
            const { integrationData } = modalAPI.getParams();
            api.embeddedIntegrations
              .set(key, integrationData)
              .then(() => updateIntegrationStore());
          },
          onClosed: () => biLogger.integrationModalClosed(biData),
          buttons: {
            primary: {
              text: modal.close || 'modal.button.close',
              height: 'medium',
              onClick: (modalAPI) => {
                modalAPI.close();
                biLogger.integrationModalCtaClick({
                  ...biData,
                  ctaType: 'close',
                });
              },
            },
            x: {
              onClick: () => biLogger.integrationModalCloseViaCloseBar(biData),
            },
          },
        });
    };

    const editAction = (status) => ({
      type: 'edit',
      label:
        status === integrationStatus.NEW
          ? detailsPage.actions.edit.label
          : 'details-page.popover.edit',
      icon: <Edit />,
      handler: () => {
        const domainConnected = api.embeddedIntegrations.isDomainConnected();
        const isWixSite = api.embeddedIntegrations.isWixSite();

        return domainConnected || isWixSite
          ? openEditModal()
          : api.openDomainUpgradeModal(biData);
      },
    });

    const deleteAction = {
      type: 'delete',
      label: detailsPage.actions.delete || 'details-page.popover.delete',
      skin: 'destructive',
      icon: (
        <Box color="R20">
          <Delete />
        </Box>
      ),
      handler: () => {
        api.openDeleteConfirmationModal({
          onDelete: (modalAPI) => {
            api.embeddedIntegrations
              .delete(key)
              .then(() => updateIntegrationStore());
            modalAPI.close();
          },
          onCancel: (modalAPI) => modalAPI.close(),
        });
      },
    };

    const updateAction = {
      type: 'update',
      label:
        (detailsPage.actions.update && detailsPage.actions.update.label) ||
        'details-page.popover.update',
      icon: <Replace />,
      handler: () => {
        api.openUpdateConfirmationModal({
          onUpdate: (modalAPI) => {
            api.embeddedIntegrations.createDraft(
              detailsPage.actions.update.newIntegrationConfig,
            );
            const integrationData = modalAPI.getParams().integrationData;
            const newIntegration = {
              ...integrationData,
              content: {
                ...integrationData.content,
                trackingId: api.embeddedIntegrations.get(key).content
                  .trackingId,
              },
            };
            api.embeddedIntegrations
              .set(newIntegration.content.type, newIntegration)
              .then(() => api.embeddedIntegrations.delete(key))
              .then(() => {
                updateIntegrationStore();
                modalAPI.close();
                redirect(newIntegration.content.type);
              });
          },
          onCancel: (modalAPI) => modalAPI.close(),
        });
      },
    };

    const enableAction = {
      type: 'enable',
      label: detailsPage.actions.enable || 'details-page.popover.enable',
      icon: <Play />,
      handler: () => {
        api.embeddedIntegrations
          .enableIntegration(key, true)
          .then(() => updateIntegrationStore());
      },
    };

    const disableAction = {
      type: 'disable',
      label: detailsPage.actions.disable || 'details-page.popover.disable',
      icon: <Pause />,
      handler: () => {
        api.embeddedIntegrations
          .enableIntegration(key, false)
          .then(() => updateIntegrationStore());
      },
    };

    function getStatus() {
      return api.embeddedIntegrations.getStatus(key);
    }

    function updateIntegrationStore() {
      api.setIntegrationProperty(key, 'status', getStatus());
      api.setIntegrationProperty(key, 'actions', getActions());
    }

    function getActions() {
      const actions = [];
      const status = getStatus();
      if (detailsPage.actions.update) {
        actions.push(updateAction);
      }
      if (status === integrationStatus.NEW) {
        actions.push(editAction(status));
      } else {
        !detailsPage.actions.edit.hideWhenComplete &&
          actions.push(editAction(status));
      }
      if (status === integrationStatus.CONNECTED) {
        actions.push(disableAction);
      }
      if (status === integrationStatus.DISCONNECTED) {
        actions.push(enableAction);
      }
      if (status !== integrationStatus.NEW) {
        actions.push(deleteAction);
      }
      return actions;
    }

    return new Promise((resolve) =>
      resolve({
        ...detailsPage.header,
        status: getStatus(),
        actions: getActions(),
        ...detailsPage.status,
      }),
    );
  },
});
